import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Avatar from '@mui/material/Avatar'
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import { Link as RouterLink } from 'react-router-dom'
import LoginProblemLinks from './LoginProblemLinks'
import useSubmitting from '../../hooks/useSubmitting'
import { selectOmniauthToken, setOmniauthToken } from '../../redux/features/user/userSlice'
import { openidRetiredPost, openidSignInEnds } from './openidRetirement'
import NoticeBox from './NoticeBox'

// Unlike the other Devise screens, this is a real full-page form post, not
// a fetch: OmniAuth's request phase (POST /user/auth/open_id) redirects the
// whole browser to the identifier's own OpenID server and back again, which
// a JS fetch can't do. The field name (`openid_url`) is fixed by the
// omniauth-openid strategy's `identifier_param` option, not something this
// screen chooses.
//
// The hidden token below is NOT the usual Rails/Devise CSRF token (that one
// is for `X-CSRF-Token` fetches, kept in userSlice as authToken) -- OmniAuth
// 2.x's own request-phase CSRF check (Rack::Protection::AuthenticityToken)
// validates against a separate session[:csrf]. Reads from Redux first, same
// pattern as authToken elsewhere, not a static meta tag alone: found
// 2026-10-14 that the meta tag (fixed at the page's last full load) goes
// stale the moment the session changes underneath it without a reload --
// sign-out in particular, including the automatic one 0.6.27.13's claim
// flow does -- bouncing a real sign-in attempt to /user/auth/new_open_id
// with no obvious cause. ApplicationController#set_csrf_token refreshes
// this on every response, the same way it already does for authToken.
export default function OpenIdSignIn() {
  const dispatch = useDispatch()
  const storedOmniauthToken = useSelector(selectOmniauthToken)
  const csrfToken = storedOmniauthToken
    || document.querySelector('meta[name=omniauth-csrf-token]')?.content || ''
  const [submitting, submit] = useSubmitting()

  useEffect(() => {
    if (!storedOmniauthToken) {
      const documentToken = document.querySelector('meta[name=omniauth-csrf-token]')?.content
      if (documentToken) {
        dispatch(setOmniauthToken(documentToken))
      }
    }
  }, [dispatch, storedOmniauthToken])

  // A real full-page form post (see the comment above the component), not
  // a fetch -- there's no response to await, the browser navigates away
  // on its own. A promise that never resolves keeps the button showing
  // its spinner for the moment before that navigation actually happens,
  // rather than resetting instantly.
  const handleSubmit = () => {
    submit(() => new Promise(() => {}))
  }

  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 9,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>

        <Typography component='h1' variant='h5'>
          OpenID Accounts
        </Typography>
        <NoticeBox>
          {/* 0.6.27.13, 2026-10-16: new accounts can't be created via
              OpenID any more (User::OmniauthCallbacksController
              #omniauth_callback) -- this says so up front, rather than
              letting someone discover it only after submitting the
              form. */}
          OpenID sign-in ends on {openidSignInEnds}. If you used OpenID on
          Stikis, enter it below to move your account to a login and
          password.{' '}
          <Link component={RouterLink} to={openidRetiredPost} sx={{ color: 'primary.dark' }}>Read why</Link>
        </NoticeBox>
        <Box
          component="form"
          action="/user/auth/open_id"
          method="post"
          onSubmit={handleSubmit}
          sx={{ mt: 1, width: '100%', maxWidth: 400 }}
          noValidate
        >
          <TextField
            margin='normal'
            required
            fullWidth
            id='openid_url'
            label='OpenID identifier'
            name='openid_url'
            autoComplete='url'
            autoFocus
          />
          <input type="hidden" name="authenticity_token" value={csrfToken} />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            loading={submitting}
            sx={{ mt: 3, mb: 2 }}
          >
            Connect
          </Button>
          <LoginProblemLinks />
        </Box>
      </Box>
    </Container>
  )
}
